import React, { useState, useEffect, useCallback, useMemo } from 'react'
import toast from 'react-hot-toast'
import api from '../services/api'
import SettlementNoticeBanner from '../components/SettlementNoticeBanner'
import AutoFitValue from '../components/financial/AutoFitValue'
import TargetCaption from '../components/financial/TargetCaption'
import { MovementHistoryTable, VoidConfirmModal } from '../components/financial/MovementHistory'
import { LegacyBalancesSection } from '../components/financial/LegacyBalances'
import { legacySubtitle } from '../components/financial/legacy'
import {
  RESPONSIVE_CSS, money, todayISO, warnIfOverpaid, saveErrorMessage,
} from '../components/financial/formatting'

// ─────────────────────────────────────────────────────────────────────────────
//   Accounts payable — lo que debemos
//
//   Un lado del antiguo FinancialPage: partners vivos y saldos heredados por
//   pagar. Su gemela es `ReceivablesPage`. Todo lo que las dos usaban vive en
//   `components/financial/`; aquí sólo queda lo que es exclusivamente del lado
//   del pago.
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
//   Descriptores de destino
//
//   Un pago a un partner vivo y un pago contra un saldo heredado del
//   Centralizador comparten todo lo que importa: el mismo PaymentOut, el mismo
//   PaymentWriteOut con `overpaid`/`overpaid_by`, y exactamente los mismos
//   PUT/DELETE `/financial/payments/{id}`. Sólo difieren en el alta y en el
//   listado. Este objeto ES esa diferencia — y es lo que permite que la lógica
//   de sobrepago y de anulación exista una sola vez en el archivo en vez de
//   duplicarse para la sección heredada.
// ─────────────────────────────────────────────────────────────────────────────
const partnerPaymentTarget = (partner) => ({
  title: partner.company_name,
  createUrl: '/financial/payments',
  createBody: { affiliate_id: partner.id },
  listUrl: '/financial/payments',
  listParams: { affiliate_id: partner.id },
  baselineLabel: 'PARTNER SHARE',
  baselineValue: partner.partner_share,
  paidLabel: 'PAID',
  pendingLabel: 'PENDING',
  initialPaid: partner.total_paid ?? 0,
  initialPending: partner.pending ?? 0,
})

const legacyPaymentTarget = (balance) => ({
  title: balance.name,
  subtitle: legacySubtitle(balance),
  createUrl: '/financial/legacy-payments',
  createBody: { legacy_balance_id: balance.id },
  listUrl: '/financial/legacy-payments',
  listParams: { legacy_balance_id: balance.id },
  baselineLabel: 'DECLARED',
  baselineValue: balance.declared,
  paidLabel: 'SETTLED',
  pendingLabel: 'PENDING',
  initialPaid: balance.settled ?? 0,
  initialPending: balance.pending ?? 0,
})

// ─────────────────────────────────────────────────────────────────────────────
//   Modal: registrar / editar un pago
// ─────────────────────────────────────────────────────────────────────────────
function PaymentFormModal({ target, editing, onClose, onSaved }) {
  const isEdit = Boolean(editing)
  const [amount, setAmount] = useState(editing ? String(editing.amount ?? '') : '')
  const [date, setDate] = useState(editing?.payment_date || todayISO())
  const [reference, setReference] = useState(editing?.reference || '')
  const [note, setNote] = useState(editing?.note || '')
  const [saving, setSaving] = useState(false)

  const save = async () => {
    const amt = Number(amount)
    if (!amt || amt <= 0) { toast.error('Enter a valid amount greater than 0'); return }
    if (!date) { toast.error('Select a payment date'); return }
    setSaving(true)
    try {
      // El alta va al endpoint del destino (vivo o heredado); la edición
      // siempre a la ruta compartida por id — un movimiento es un movimiento y
      // la fila ya sabe qué saldo salda.
      const res = isEdit
        ? await api.put(`/financial/payments/${editing.id}`, {
            amount: amt, payment_date: date, reference: reference || null, note: note || null,
          })
        : await api.post(target.createUrl, {
            ...target.createBody, amount: amt,
            payment_date: date, reference: reference || null, note: note || null,
          })
      toast.success(isEdit ? 'Payment updated' : 'Payment registered')
      warnIfOverpaid(res.data, 'Payment')
      onSaved(res.data)
    } catch (err) {
      toast.error(saveErrorMessage(err, 'payment'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="card fin-modal" style={{ maxWidth: 460, width: '100%' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <h3 style={{ margin: 0 }}>{isEdit ? 'Edit payment' : 'Register payment'}</h3>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}>✕</button>
        </div>
        <TargetCaption target={target} style={{ marginTop: 0 }} />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
          <div>
            <label className="form-label">Amount (USD)</label>
            <input className="input fin-input" type="number" min="0" step="0.01" placeholder="0.00"
              value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          <div>
            <label className="form-label">Payment date</label>
            <input className="input fin-input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div>
            <label className="form-label">Transaction reference <span style={{ color: 'var(--text-muted)' }}>(optional)</span></label>
            <input className="input fin-input" type="text" placeholder="e.g. wire #, confirmation code"
              value={reference} onChange={(e) => setReference(e.target.value)} />
          </div>
          <div>
            <label className="form-label">Note <span style={{ color: 'var(--text-muted)' }}>(optional)</span></label>
            <input className="input fin-input" type="text" placeholder="Any note"
              value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
          <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : (isEdit ? 'Update' : 'Register')}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
//   Modal: histórico de pagos (con editar / anular)
// ─────────────────────────────────────────────────────────────────────────────
function PaymentHistoryModal({ target, onClose, onChanged }) {
  const [payments, setPayments] = useState([])
  const [loading, setLoading] = useState(true)
  // Los tres importes de la cabecera vienen del backend y sólo del backend:
  // `partner_share`/`pending` del summary, `total_paid` del listado, y
  // `pending` recalculado por el propio backend tras cada escritura. Ninguno
  // se suma en cliente — la lista incluye filas anuladas y sumarla daría un
  // total que el backend no reconoce.
  const [totalPaid, setTotalPaid] = useState(target.initialPaid)
  const [pending, setPending] = useState(target.initialPending)
  const [editing, setEditing] = useState(null)
  const [voiding, setVoiding] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      // include_voided: las anuladas son parte de la historia y se muestran
      // intercaladas. No contaminan el saldo: `total_paid` suma siempre sólo
      // las activas (financial/aggregates.py, payments_for).
      const res = await api.get(target.listUrl, {
        params: { ...target.listParams, include_voided: true },
      })
      setPayments(res.data?.payments || [])
      setTotalPaid(res.data?.total_paid ?? 0)
    } catch {
      toast.error('Could not load payment history')
    } finally {
      setLoading(false)
    }
  }, [target])

  useEffect(() => { load() }, [load])

  const voidPayment = async (reason) => {
    try {
      // Ruta compartida por id, igual que la edición: sirve tanto al pago a un
      // partner vivo como al de un saldo heredado.
      const res = await api.delete(`/financial/payments/${voiding.id}`, { data: { reason } })
      toast.success('Payment annulled')
      setPending(res.data?.pending ?? pending)
      setVoiding(null)
      await load()
      onChanged()
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not annul payment')
      // 409 = otra pestaña la anuló primero. Recargamos para que la fila
      // muestre su estado real en vez de volver a ofrecer los botones.
      if (err?.response?.status === 409) {
        setVoiding(null)
        await load()
        onChanged()
      }
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="card fin-modal" style={{ maxWidth: 640, width: '100%' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ margin: 0 }}>Payment history</h3>
            <TargetCaption target={target} style={{ margin: '2px 0 0' }} />
          </div>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}>✕</button>
        </div>

        <div style={{ display: 'flex', gap: 20, margin: '14px 0', flexWrap: 'wrap' }}>
          <div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{target.baselineLabel}</div>
            <div style={{ fontWeight: 700, color: 'var(--amber)' }}>{money(target.baselineValue)}</div></div>
          <div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{target.paidLabel}</div>
            <div style={{ fontWeight: 700, color: 'var(--green)' }}>{money(totalPaid)}</div></div>
          <div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{target.pendingLabel}</div>
            <div style={{ fontWeight: 700, color: 'var(--red)' }}>{money(pending)}</div></div>
        </div>

        {loading ? (
          <p style={{ color: 'var(--text-muted)' }}>Loading…</p>
        ) : payments.length === 0 ? (
          <p style={{ color: 'var(--text-muted)' }}>No payments registered yet.</p>
        ) : (
          <MovementHistoryTable
            rows={payments}
            dateField="payment_date"
            onEdit={setEditing}
            onVoid={setVoiding}
          />
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
        </div>
      </div>

      {editing && (
        <PaymentFormModal
          target={target} editing={editing}
          onClose={() => setEditing(null)}
          onSaved={(result) => {
            setEditing(null)
            if (result) setPending(result.pending)
            load()
            onChanged()
          }}
        />
      )}

      {voiding && (
        <VoidConfirmModal
          noun="payment"
          onCancel={() => setVoiding(null)}
          onConfirm={voidPayment}
        />
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
//   Tarjeta por partner
// ─────────────────────────────────────────────────────────────────────────────
function PartnerFinanceCard({ partner, onRegister, onHistory }) {
  // Porcentaje de barra de progreso, no un importe: presentación pura sobre
  // dos cifras que ya vienen calculadas del backend.
  const paidPct = partner.partner_share > 0
    ? Math.min(100, Math.round((partner.total_paid / partner.partner_share) * 100))
    : 0

  return (
    <div className="card fade-in partners-card">
      <div className="partners-card-head">
        <div className="partners-card-title-wrap">
          <div className="partners-card-title">{partner.company_name}</div>
          <div className="partners-card-subtitle">{partner.id} · {partner.split_label}</div>
        </div>
      </div>

      <div className="fin-card-metrics" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr) minmax(0,1fr)', gap: 8, marginTop: 4 }}>
        <div className="partners-metric-box" style={{ minWidth: 0, overflow: 'hidden' }}>
          <AutoFitValue>{money(partner.postback_total)}</AutoFitValue>
          <div className="partners-metric-label">Revenue</div>
        </div>
        <div className="partners-metric-box" style={{ minWidth: 0, overflow: 'hidden' }}>
          <AutoFitValue color="var(--blue)">{money(partner.our_share)}</AutoFitValue>
          <div className="partners-metric-label">Our share</div>
        </div>
        <div className="partners-metric-box" style={{ minWidth: 0, overflow: 'hidden' }}>
          <AutoFitValue color="var(--amber)">{money(partner.partner_share)}</AutoFitValue>
          <div className="partners-metric-label">Partner share</div>
        </div>
      </div>

      {partner.partner_share > 0 && (
        <div style={{ marginTop: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
            <span style={{ color: 'var(--green)' }}>Paid {money(partner.total_paid)}</span>
            <span style={{ color: 'var(--red)' }}>Pending {money(partner.pending)}</span>
          </div>
          <div style={{ height: 8, borderRadius: 4, background: 'var(--bg-raised)', overflow: 'hidden' }}>
            <div style={{ width: `${paidPct}%`, height: '100%', background: 'var(--green)' }} />
          </div>
        </div>
      )}

      <div className="fin-card-actions" style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button className="btn btn-primary btn-sm" style={{ flex: 1 }}
          onClick={() => onRegister(partner)}
          disabled={partner.partner_share <= 0}
          title={partner.partner_share <= 0 ? 'No partner share to pay' : 'Register a payment'}>
          Register payment
        </button>
        <button className="btn btn-ghost btn-sm" style={{ flex: 1 }} onClick={() => onHistory(partner)}>
          Payment history
        </button>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
//   Página
// ─────────────────────────────────────────────────────────────────────────────
export default function PayablesPage() {
  const [data, setData] = useState({ partners: [], totals: {} })
  const [legacy, setLegacy] = useState({ payables: [], totals: {}, error: false })
  const [loading, setLoading] = useState(true)
  const [registerFor, setRegisterFor] = useState(null)
  const [historyFor, setHistoryFor] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [pRes, lRes] = await Promise.all([
        api.get('/financial/summary'),
        // Los saldos heredados son un añadido; los partners vivos son lo
        // principal. Si el endpoint falla, la página no puede quedarse en
        // blanco — se marca el fallo y la sección lo dice en su sitio.
        //
        // `/financial/legacy-summary` no acepta filtro por lado: devuelve
        // payables y receivables juntos y esta página se queda con su mitad.
        // /receivables hace lo propio con la otra.
        api.get('/financial/legacy-summary').catch(() => null),
      ])
      setData({ partners: pRes.data?.partners || [], totals: pRes.data?.totals || {} })
      // `as_of` y `as_of_mixed` del envelope se descartan a propósito: los
      // calcula el backend sobre los dos lados juntos y aquí sólo se enseña
      // uno. LegacyBalancesSection los deriva de estas filas — ver cutOffOf()
      // en components/financial/legacy.js.
      setLegacy({
        payables: lRes?.data?.payables || [],
        totals: lRes?.data?.totals || {},
        error: lRes === null,
      })
    } catch {
      toast.error('Could not load payables')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const t = data.totals || {}
  const lt = legacy.totals || {}

  // Algunas contrapartes heredadas existen también en la sección viva de
  // arriba, con saldos distintos y sin relación. Marcarlas es lo que evita que
  // alguien vea "Partners Edge" dos veces con cifras diferentes y lo lea como
  // un descuadre. El cruce es por nombre porque es lo único que comparten: un
  // saldo heredado no referencia al affiliate actual — sus leads nunca se
  // migraron. Si el backend renombra a una, el badge simplemente deja de
  // salir; nunca aparece uno falso.
  //
  // Deliberadamente sólo contra los partners vivos. Antes de partir la página
  // el cruce era indiscriminado (partners Y buyers contra los dos lados
  // heredados), así que un payable heredado se marcaba "Also above" porque su
  // nombre coincidía con el de un buyer — una contraparte que no aparece en
  // ninguna parte de esta página. Ahora cada lado se cruza sólo con el suyo.
  const liveNames = useMemo(
    () => new Set(
      data.partners
        .map((x) => x.company_name)
        .filter(Boolean)
        .map((n) => String(n).trim().toLowerCase()),
    ),
    [data.partners],
  )

  return (
    <div className="fade-in">
      <style>{RESPONSIVE_CSS}</style>

      {/* No range on purpose. `/financial/summary` and
          `/financial/legacy-summary` take no date parameter - what this page
          shows are lifetime cumulative balances, not a period - and the
          backend reads an absent bound as an open end, so this asks for every
          notice that bears on them. Narrowing it to, say, the current year
          would hide exactly the carried-over agreement that explains a pending
          balance. /receivables mounts the same banner over its own endpoints;
          the scoping is server-side (companies/settlements.py, notices_for()),
          so each page gets what its caller may see rather than a copy filtered
          here. */}
      <div style={{ marginBottom: 20 }}>
        <SettlementNoticeBanner />
      </div>

      <div className="page-header">
        <div className="hero-kicker">Financial</div>
        <h1>Accounts payable</h1>
        <p style={{ color: 'var(--text-muted)', maxWidth: 720 }}>
          Revenue per partner, the split that applies to it, and the payments made against it.
        </p>
      </div>

      <div className="fin-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 24 }}>
        {[
          { label: 'Total revenue', value: money(t.postback_total), color: 'var(--text-primary)' },
          { label: 'Our share', value: money(t.our_share), color: 'var(--blue)' },
          { label: 'Partner share', value: money(t.partner_share), color: 'var(--amber)' },
          { label: 'Paid', value: money(t.total_paid), color: 'var(--green)' },
          { label: 'Pending', value: money(t.pending), color: 'var(--red)' },
        ].map((s) => (
          <div key={s.label} className="card" style={{ padding: '18px 12px', textAlign: 'center', minWidth: 0, overflow: 'hidden' }}>
            <AutoFitValue color={s.color} max={24} min={13}>{s.value}</AutoFitValue>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <p style={{ color: 'var(--text-muted)' }}>Loading…</p>
      ) : (
        <div className="fin-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 16 }}>
          {data.partners.map((p) => (
            <PartnerFinanceCard
              key={p.id}
              partner={p}
              onRegister={(x) => setRegisterFor(partnerPaymentTarget(x))}
              onHistory={(x) => setHistoryFor(partnerPaymentTarget(x))}
            />
          ))}
        </div>
      )}

      {!loading && (
        <LegacyBalancesSection
          balances={legacy.payables}
          error={legacy.error}
          liveNames={liveNames}
          title="Payables · we owe them"
          caption="Left unpaid to partners when the previous system was switched off."
          stats={[
            { label: 'Declared', value: lt.payable_declared, color: 'var(--amber)' },
            { label: 'Settled', value: lt.payable_settled, color: 'var(--green)' },
            { label: 'Pending', value: lt.payable_pending, color: 'var(--red)' },
          ]}
          actionLabel="Register payment"
          historyLabel="Payment history"
          onRegister={(b) => setRegisterFor(legacyPaymentTarget(b))}
          onHistory={(b) => setHistoryFor(legacyPaymentTarget(b))}
        />
      )}

      {registerFor && (
        <PaymentFormModal
          target={registerFor}
          editing={null}
          onClose={() => setRegisterFor(null)}
          onSaved={() => { setRegisterFor(null); load() }}
        />
      )}

      {historyFor && (
        <PaymentHistoryModal
          target={historyFor}
          onClose={() => setHistoryFor(null)}
          onChanged={load}
        />
      )}
    </div>
  )
}
